import { PlainDate } from "@sa-nu/shared-utils/date";
import { createParser, useQueryStates } from "nuqs";
import { useCallback, useEffect, useMemo } from "react";

import type {
  CICOSelection,
  ReservationCalendarNavigationSize,
} from "../reservation-calendar";

const parseAsPlainDate = createParser({
  parse: (value) => {
    try {
      return PlainDate.from(value);
    } catch {
      return null;
    }
  },
  serialize: (value: PlainDate) => value.toString(),
});

const cicoParams = {
  ci: parseAsPlainDate,
  co: parseAsPlainDate,
};

/**
 * CI/CO（チェックイン/チェックアウト）選択の状態管理フック
 *
 * - URLクエリパラメータ ci/co と双方向同期（nuqs）
 * - useReservationCalendarDateRange とは完全に独立
 * - shiftCICO: シェブロン操作時にCI/COを同日数分平行移動（泊数維持）
 * - bounds check: minNight/maxNight/navigationSize 範囲外には設定しない
 * - マウント時: 初期値が範囲外なら URL をクリア（以降は setCICO 経由でのバリデーション）
 */
export function useCICOState({
  minNight,
  maxNight,
  navigationSize,
  onInit,
}: {
  minNight: PlainDate;
  maxNight: PlainDate;
  navigationSize: ReservationCalendarNavigationSize;
  /** URL由来の初期CICOが有効だった場合にマウント時1回だけ呼ばれるコールバック */
  onInit?: (cico: CICOSelection) => void;
}) {
  const [{ ci, co }, setParams] = useQueryStates(cicoParams);

  const cico: CICOSelection | null = useMemo(() => {
    if (!ci || !co) return null;
    return { checkIn: ci, checkOut: co };
  }, [ci, co]);

  // マウント時のみ: URL 由来の初期値を判定し、範囲外ならクリア、有効なら onInit を呼ぶ
  useEffect(() => {
    if (!cico) return;
    if (!isCICOWithinBounds(cico, { minNight, maxNight, navigationSize })) {
      void setParams({ ci: null, co: null });
      return;
    }
    onInit?.(cico);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時1回のみ実行
  }, []);

  const setCICO = useCallback(
    (value: CICOSelection | null) => {
      if (value === null) {
        void setParams({ ci: null, co: null });
        return;
      }
      if (!isCICOWithinBounds(value, { minNight, maxNight, navigationSize })) {
        return;
      }
      void setParams({ ci: value.checkIn, co: value.checkOut });
    },
    [minNight, maxNight, navigationSize, setParams],
  );

  const shiftCICO = useCallback(
    (deltaDays: number) => {
      if (!cico) return;
      const newCheckIn = cico.checkIn.add({ days: deltaDays });
      const newCheckOut = cico.checkOut.add({ days: deltaDays });
      // bounds 外なら書き込まない（nuqs は updater で prev を返しても URL write するため closure 側で弾く）
      if (PlainDate.compare(newCheckIn, minNight) < 0) return;
      if (PlainDate.compare(newCheckOut.subtract({ days: 1 }), maxNight) > 0) {
        return;
      }
      void setParams({ ci: newCheckIn, co: newCheckOut });
    },
    [cico, minNight, maxNight, setParams],
  );

  const clearCICO = useCallback(() => {
    void setParams({ ci: null, co: null });
  }, [setParams]);

  return { cico, setCICO, shiftCICO, clearCICO };
}

/**
 * CICO が予約可能範囲内に収まっているかを判定する
 * - nights が 0 以下（checkIn >= checkOut）は不正
 * - nights が navigationSize を超えると表示範囲外
 * - minNight/maxNight の境界内でなければ不正
 */
function isCICOWithinBounds(
  cico: CICOSelection,
  {
    minNight,
    maxNight,
    navigationSize,
  }: {
    minNight: PlainDate;
    maxNight: PlainDate;
    navigationSize: ReservationCalendarNavigationSize;
  },
): boolean {
  const nights = cico.checkIn.until(cico.checkOut).total({ unit: "day" });
  if (nights <= 0 || nights > navigationSize) return false;
  if (PlainDate.compare(cico.checkIn, minNight) < 0) return false;
  if (PlainDate.compare(cico.checkOut.subtract({ days: 1 }), maxNight) > 0)
    return false;
  return true;
}
